import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Send, Heart, Sparkles, BookmarkPlus, Plus, MessageSquareText, Trash2, PanelLeft } from "lucide-react";

const prompts = [
    "I'm feeling really down today…",
    "I can't sleep and my mind won't stop.",
    "I feel invisible lately.",
    "I need someone to just listen.",
];

const timeAgo = (iso) => {
    const then = new Date(iso).getTime();
    const secs = Math.floor((Date.now() - then) / 1000);
    if (secs < 60) return "just now";
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    if (secs < 86400 * 7) return `${Math.floor(secs / 86400)}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

function SessionList({ sessions, currentId, onSelect, onDelete, onNew, compact }) {
    return (
        <div className={`flex flex-col h-full w-full min-w-0 ${compact ? "" : "border-r border-border bg-card/40"}`}>
            <div className="p-4 flex items-center justify-between border-b border-border">
                <h2 className="font-heading text-sm font-[700] tracking-tight uppercase text-muted-foreground">Conversations</h2>
                <Button size="sm" variant="ghost" onClick={onNew} className="rounded-full h-8" data-testid="sidebar-new-chat-btn">
                    <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> New
                </Button>
            </div>
            <div className="flex-1 overflow-y-auto chat-scroll p-3 space-y-1" data-testid="chat-sessions-list">
                {sessions.length === 0 && (
                    <div className="text-xs text-muted-foreground p-4 text-center leading-relaxed">
                        Your past conversations will show up here after your first chat.
                    </div>
                )}
                {sessions.map((s) => {
                    const active = s.session_id === currentId;
                    return (
                        <div
                            key={s.session_id}
                            className={`group flex items-start gap-2 p-3 rounded-2xl cursor-pointer transition-colors duration-200 ${
                                active ? "bg-primary/15 border border-primary/30" : "hover:bg-accent border border-transparent"
                            }`}
                            onClick={() => onSelect(s.session_id)}
                            data-testid={`session-item-${s.session_id}`}
                        >
                            <MessageSquareText className={`w-4 h-4 mt-0.5 flex-shrink-0 ${active ? "text-primary" : "text-muted-foreground"}`} strokeWidth={1.5} />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate leading-tight mb-1">
                                    {s.title || "New conversation"}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">
                                    {timeAgo(s.updated_at)} · {s.count} msg{s.count === 1 ? "" : "s"}
                                </div>
                            </div>
                            <button
                                onClick={(e) => { e.stopPropagation(); onDelete(s.session_id); }}
                                className="opacity-0 group-hover:opacity-100 p-1.5 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-opacity duration-200 flex-shrink-0"
                                data-testid={`session-delete-${s.session_id}`}
                                aria-label="Delete conversation"
                            >
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function Chat() {
    const { sessionId: sessionIdParam } = useParams();
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sessionId, setSessionId] = useState(sessionIdParam || null);
    const [streaming, setStreaming] = useState(false);
    const [streamingText, setStreamingText] = useState("");
    const [sessions, setSessions] = useState([]);
    const [sheetOpen, setSheetOpen] = useState(false);
    const scrollRef = useRef(null);

    // Load sessions list
    const loadSessions = useCallback(async () => {
        try {
            const { data } = await api.get("/chat/sessions");
            setSessions(data.sessions || []);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => { loadSessions(); }, [loadSessions]);

    // Load history when URL session changes
    useEffect(() => {
        if (!sessionIdParam) {
            setMessages([]);
            setSessionId(null);
            setStreamingText("");
            return;
        }
        setSessionId(sessionIdParam);
        (async () => {
            try {
                const { data } = await api.get(`/chat/history?session_id=${encodeURIComponent(sessionIdParam)}`);
                setMessages((data.messages || []).map(m => ({ id: m.id, role: m.role, text: m.text })));
            } catch {
                toast.error("Could not load that conversation");
            }
        })();
    }, [sessionIdParam]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, streamingText]);

    const send = async (textArg) => {
        const text = (textArg ?? input).trim();
        if (!text || streaming) return;
        setInput("");
        const userMsg = { id: `u-${Date.now()}`, role: "user", text };
        setMessages(prev => [...prev, userMsg]);
        setStreaming(true);
        setStreamingText("");

        try {
            const token = getToken();
            const res = await fetch(`${API}/chat/stream`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                credentials: "include",
                body: JSON.stringify({ text, session_id: sessionId }),
            });
            if (!res.ok || !res.body) throw new Error("Chat request failed");
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let collected = "";
            let localSessionId = sessionId;
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop();
                for (const part of parts) {
                    const line = part.trim();
                    if (!line.startsWith("data:")) continue;
                    try {
                        const payload = JSON.parse(line.slice(5).trim());
                        if (payload.type === "session") {
                            if (!sessionId) {
                                localSessionId = payload.session_id;
                                setSessionId(payload.session_id);
                                // reflect in URL so refresh keeps the thread
                                navigate(`/app/chat/${payload.session_id}`, { replace: true });
                            }
                        } else if (payload.type === "delta") {
                            collected += payload.content;
                            setStreamingText(collected);
                        } else if (payload.type === "done") {
                            setMessages(prev => [...prev, { id: payload.message_id, role: "assistant", text: collected }]);
                            setStreamingText("");
                        } else if (payload.type === "error") {
                            toast.error("Something went wrong. Please try again.");
                        }
                    } catch { /* ignore malformed */ }
                }
            }
            // Refresh sessions list after a completed exchange
            loadSessions();
        } catch (e) {
            toast.error("Connection hiccup. Please try again.");
            console.error(e);
        } finally {
            setStreaming(false);
        }
    };

    const saveFavorite = async (text) => {
        try {
            await api.post("/favorites", { text, source: "chat" });
            toast.success("Saved to favorites");
        } catch {
            toast.error("Could not save");
        }
    };

    const newChat = () => {
        setMessages([]);
        setSessionId(null);
        setStreamingText("");
        navigate("/app/chat", { replace: true });
        setSheetOpen(false);
    };

    const openSession = (id) => {
        navigate(`/app/chat/${id}`);
        setSheetOpen(false);
    };

    const deleteSession = async (id) => {
        try {
            await api.delete(`/chat/sessions/${id}`);
            setSessions(prev => prev.filter(s => s.session_id !== id));
            toast.success("Conversation removed");
            if (id === sessionId) {
                newChat();
            }
        } catch {
            toast.error("Could not delete");
        }
    };

    return (
        <div className="flex h-[calc(100vh-5rem)] lg:h-screen">
            {/* Desktop sessions sidebar */}
            <aside className="hidden md:flex w-72 flex-shrink-0 min-w-0" data-testid="chat-sessions-sidebar">
                <SessionList
                    sessions={sessions}
                    currentId={sessionId}
                    onSelect={openSession}
                    onDelete={deleteSession}
                    onNew={newChat}
                />
            </aside>

            {/* Chat pane */}
            <div className="flex flex-col flex-1 min-w-0">
                {/* Header */}
                <div className="px-4 sm:px-8 py-5 border-b border-border bg-background/70 backdrop-blur-xl flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        {/* Mobile sessions trigger */}
                        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="icon" className="md:hidden rounded-full" data-testid="chat-sessions-mobile-trigger">
                                    <PanelLeft className="w-5 h-5" strokeWidth={1.5} />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="left" className="p-0 w-80">
                                <SheetHeader className="sr-only">
                                    <SheetTitle>Conversations</SheetTitle>
                                    <SheetDescription>Your past conversations with CheerUp!</SheetDescription>
                                </SheetHeader>
                                <SessionList
                                    sessions={sessions}
                                    currentId={sessionId}
                                    onSelect={openSession}
                                    onDelete={deleteSession}
                                    onNew={newChat}
                                    compact
                                />
                            </SheetContent>
                        </Sheet>

                        <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center shadow-soft flex-shrink-0">
                            <Heart className="w-5 h-5 text-primary-foreground" strokeWidth={2} fill="currentColor" />
                        </div>
                        <div className="min-w-0">
                            <div className="font-heading text-lg font-[700] tracking-tight truncate">Your Companion</div>
                            <div className="text-xs text-muted-foreground truncate">Here to listen, gently.</div>
                        </div>
                    </div>
                    <Button variant="outline" size="sm" className="rounded-full bg-background flex-shrink-0" onClick={newChat} data-testid="new-chat-btn">
                        <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> New chat
                    </Button>
                </div>

                {/* Messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto chat-scroll px-4 sm:px-10 py-8">
                    <div className="max-w-3xl mx-auto space-y-6">
                        {messages.length === 0 && !streaming && (
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center py-10">
                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent border border-border mb-6">
                                    <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
                                    <span className="text-sm font-medium">Whatever you're carrying, you don't have to carry it alone.</span>
                                </div>
                                <h2 className="font-heading text-3xl sm:text-4xl font-[300] italic tracking-tight mb-2">What's on your heart?</h2>
                                <p className="text-muted-foreground mb-8">Start typing below, or try a gentle opener.</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
                                    {prompts.map(p => (
                                        <button
                                            key={p}
                                            onClick={() => send(p)}
                                            data-testid={`chat-prompt-${p.slice(0, 10)}`}
                                            className="text-left p-5 rounded-2xl bg-card border border-border hover:-translate-y-1 hover:shadow-soft transition-transform duration-300"
                                        >
                                            <span className="text-sm leading-relaxed">{p}</span>
                                        </button>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        <AnimatePresence initial={false}>
                            {messages.map(m => (
                                <motion.div
                                    key={m.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4 }}
                                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                                >
                                    <div className="max-w-[85%] group">
                                        <div className={`px-5 py-4 rounded-3xl leading-relaxed whitespace-pre-wrap ${
                                            m.role === "user"
                                                ? "bg-primary text-primary-foreground rounded-br-lg"
                                                : "bg-card border border-border rounded-bl-lg text-foreground"
                                        }`}>
                                            {m.text}
                                        </div>
                                        {m.role === "assistant" && m.text && (
                                            <button
                                                onClick={() => saveFavorite(m.text)}
                                                className="mt-2 ml-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                                data-testid="save-favorite-btn"
                                            >
                                                <BookmarkPlus className="w-3.5 h-3.5" strokeWidth={1.5} />
                                                Save
                                            </button>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>

                        {streaming && (
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex justify-start">
                                <div className="max-w-[85%] px-5 py-4 rounded-3xl rounded-bl-lg bg-card border border-border">
                                    {streamingText ? (
                                        <span className="whitespace-pre-wrap leading-relaxed">{streamingText}</span>
                                    ) : (
                                        <span><span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" /></span>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </div>
                </div>

                {/* Input */}
                <div className="border-t border-border bg-background/80 backdrop-blur-xl px-4 sm:px-10 py-5">
                    <div className="max-w-3xl mx-auto flex items-end gap-3">
                        <Textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    send();
                                }
                            }}
                            placeholder="Say anything. I'm listening."
                            disabled={streaming}
                            rows={1}
                            className="flex-1 min-h-[56px] max-h-40 rounded-3xl px-5 py-4 border-border bg-card resize-none"
                            data-testid="chat-input"
                        />
                        <Button
                            onClick={() => send()}
                            disabled={streaming || !input.trim()}
                            size="icon"
                            className="w-14 h-14 rounded-2xl shadow-soft flex-shrink-0"
                            data-testid="chat-send-btn"
                        >
                            <Send className="w-5 h-5" strokeWidth={1.5} />
                        </Button>
                    </div>
                    <p className="text-center text-xs text-muted-foreground mt-3 max-w-2xl mx-auto">
                        CheerUp! is a companion, not a substitute for professional care. If you're in crisis, please contact your local helpline (US: call/text 988).
                    </p>
                </div>
            </div>
        </div>
    );
}
